import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, FileText, AlertTriangle, Upload, Download, Users, Eye } from 'lucide-react';
import { ContractTemplate, TemplateType } from '@/types/business-opportunities';
import { getContractTemplate, createContractTemplate, updateContractTemplate } from '@/services/business-opportunities';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Template parcial para criar/editar, sem campos obrigatórios do BaseEntity
type EditableTemplate = Omit<Partial<ContractTemplate>, 'created_by' | 'updated_by'> & {
  id: string;
  name: string;
  template_type: TemplateType;
  html_content?: string;
  docx_file_url?: string;
  legal_representatives?: string[];
  witnesses?: string[];
  created_at: string;
  updated_at: string;
  is_active: boolean;
  description: string;
};

type User = {
  id: string;
  email: string;
  role?: string;
}

const ContractTemplateForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNew = !id || id === 'new';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [template, setTemplate] = useState<EditableTemplate>({
    id: '',
    name: '',
    description: '',
    template_type: 'services' as TemplateType,
    html_content: '',
    docx_file_url: '',
    legal_representatives: [],
    witnesses: [],
    is_active: true,
    created_at: '',
    updated_at: '',
  });
  
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [signedDownloadUrl, setSignedDownloadUrl] = useState<string>('');

  // Tipos de templates disponíveis
  const templateTypes = [
    { value: 'services' as TemplateType, label: 'Serviço' },
    { value: 'products' as TemplateType, label: 'Produto' },
    { value: 'licensing' as TemplateType, label: 'Licenciamento' },
    { value: 'consultancy' as TemplateType, label: 'Consultoria' },
    { value: 'subscription' as TemplateType, label: 'Assinatura' }
  ];

  useEffect(() => {
    if (!isNew) {
      fetchTemplate();
    }
    fetchUsers();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      setIsLoading(true);

      const data = await getContractTemplate(id as string);

      if (data) {
        setTemplate({
          id: data.id,
          name: data.name,
          description: data.description || '',
          template_type: data.template_type as TemplateType,
          html_content: data.html_content,
          docx_file_url: data.docx_file_url,
          legal_representatives: data.legal_representatives || [],
          witnesses: data.witnesses || [],
          is_active: data.is_active,
          created_at: data.created_at,
          updated_at: data.updated_at
        });
      }
    } catch (error) {
      console.error('Erro ao buscar template:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar o template de contrato.',
        variant: 'destructive',
      });
      navigate('/contract-templates');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const { data, error } = await supabase
        .from('users')
        .select('id, email, role')
        .eq('is_active', true)
        .order('email');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar a lista de usuários.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTemplate(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (checked: boolean) => {
    setTemplate(prev => ({ ...prev, is_active: checked }));
  };

  const handleSelectChange = (value: TemplateType) => {
    setTemplate(prev => ({ ...prev, template_type: value }));
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        toast({
          title: 'Formato inválido',
          description: 'Por favor, selecione um arquivo no formato .docx',
          variant: 'destructive',
        });
        return;
      }
      setSelectedFile(file);
    }
  };
  
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!template.name || !template.template_type) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha todos os campos obrigatórios.',
        variant: 'destructive',
      });
      return;
    }
    
    if (isNew && !selectedFile) {
      toast({
        title: 'Arquivo necessário',
        description: 'Por favor, selecione um arquivo de template (.docx)',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      let savedTemplate;

      if (isNew) {
        // Criar novo template usando a função de serviço
        savedTemplate = await createContractTemplate(template, selectedFile || undefined);
      } else {
        // Atualizar template existente usando a função de serviço
        savedTemplate = await updateContractTemplate(id as string, template, selectedFile || undefined);
      }

      toast({
        title: isNew ? 'Template criado' : 'Template atualizado',
        description: isNew
          ? 'O template de contrato foi criado com sucesso.'
          : 'O template de contrato foi atualizado com sucesso.',
      });

      navigate('/contract-templates');
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast({
        title: 'Erro',
        description: `Não foi possível ${isNew ? 'criar' : 'atualizar'} o template de contrato.`,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      if (!template.docx_file_url) {
        toast({
          title: "Erro",
          description: "Nenhum arquivo de template disponível para download.",
          variant: "destructive",
        });
        return;
      }

      // Extrair o caminho do arquivo a partir da URL
      const urlParts = template.docx_file_url.split('/');
      const filePath = urlParts[urlParts.length - 2] + '/' + urlParts[urlParts.length - 1];
      // Se a URL estiver formatada como 'storage/v1/object/public/contracts/templates/id.docx'
      // Alternativa: const filePath = template.docx_file_url.split('contracts/')[1];

      toast({
        title: "Download iniciado",
        description: "Preparando o arquivo para download...",
      });

      const { data, error } = await supabase
        .storage
        .from('contracts')
        .download(`templates/${template.id}.docx`);

      if (error) {
        console.error('Erro ao baixar arquivo:', error);
        toast({
          title: "Erro no download",
          description: `Não foi possível baixar o arquivo: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      // Criar um blob a partir dos dados e gerar uma URL para o download
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);

      // Criar um link de download e executá-lo
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${template.name.replace(/\s+/g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      
      // Limpar recursos
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Download concluído",
        description: "O arquivo foi baixado com sucesso.",
      });
    } catch (err) {
      console.error('Erro ao processar download:', err);
      toast({
        title: "Erro no download",
        description: "Ocorreu um erro inesperado durante o download.",
        variant: "destructive",
      });
    }
  };

  const handleLegalRepresentativesChange = (selectedValues: string[]) => {
    setTemplate(prev => ({
      ...prev,
      legal_representatives: selectedValues
    }));
  };

  const handleWitnessesChange = (selectedValues: string[]) => {
    setTemplate(prev => ({
      ...prev,
      witnesses: selectedValues
    }));
  };

  const handlePreviewTemplate = async () => {
    try {
      if (!template.docx_file_url) {
        toast({
          title: "Preview indisponível",
          description: "Não há arquivo de template disponível para visualizar.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Preparando preview",
        description: "Configurando visualização do documento...",
      });

      // Criar uma URL assinada com validade de 1 hora para acessar o arquivo
      const { data: signedUrlData, error: signedUrlError } = await supabase
        .storage
        .from('contracts')
        .createSignedUrl(`templates/${template.id}.docx`, 60 * 60);

      if (signedUrlError) {
        console.error('Erro ao gerar URL assinada:', signedUrlError);
        toast({
          title: "Erro no preview",
          description: "Não foi possível gerar uma URL de acesso para o documento.",
          variant: "destructive",
        });
        return;
      }

      // Usar o serviço do Office Online Viewer com a URL assinada
      const encodedUrl = encodeURIComponent(signedUrlData.signedUrl);
      const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;

      setPreviewUrl(viewerUrl);
      setSignedDownloadUrl(signedUrlData.signedUrl);
      setShowPreviewDialog(true);

    } catch (err) {
      console.error('Erro ao preparar preview:', err);
      toast({
        title: "Erro no preview",
        description: "Não foi possível preparar a visualização do documento.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-full px-4 py-6 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full px-4 py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={() => navigate('/contract-templates')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold">
              {isNew ? 'Novo Template de Contrato' : 'Editar Template de Contrato'}
            </h1>
            <p className="text-muted-foreground">
              {isNew 
                ? 'Crie um novo template para gerar contratos.'
                : 'Edite as informações do template de contrato.'}
            </p>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !template.name || (isNew && !selectedFile)}
            className="flex items-center"
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Salvando...' : 'Salvar Template'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Detalhes do Template</CardTitle>
              <CardDescription>
                Informações básicas sobre o template de contrato.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Template <span className="text-red-500">*</span></Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Ex: Contrato Padrão de Serviços"
                    value={template.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="template_type">Tipo de Template <span className="text-red-500">*</span></Label>
                  <Select 
                    value={template.template_type} 
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {templateTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Descreva o propósito deste template..."
                  value={template.description}
                  onChange={handleChange}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <div className="space-y-2">
                  <Label>Arquivo de Template (.docx) <span className="text-red-500">*</span></Label>
                  <div className="border rounded-md p-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden"
                    />
                    
                    {selectedFile ? (
                      <div className="flex items-center">
                        <FileText className="h-8 w-8 mr-3 text-blue-500" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-extrabold truncate">{selectedFile.name}</h3>
                          <p className="text-xs text-gray-500">
                            {(selectedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleFileButtonClick}
                          className="ml-2"
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : template.docx_file_url ? (
                      <div className="flex items-center">
                        <FileText className="h-8 w-8 mr-3 text-green-500" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-extrabold">Template atual</h3>
                          <p className="text-xs text-gray-500">
                            {template.docx_file_url.split('/').pop()}
                          </p>
                        </div>
                        <div className="flex space-x-1">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleFileButtonClick}
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handlePreviewTemplate}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleDownloadTemplate}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center flex-col">
                        <FileText className="h-8 w-8 mb-2 text-gray-400" />
                        <Button 
                          variant="outline" 
                          onClick={handleFileButtonClick}
                          className="flex items-center"
                          size="sm"
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Selecionar arquivo
                        </Button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Formatos aceitos: .docx (Word) - Tamanho máximo: 10MB
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Instruções</Label>
                  <div className="border rounded-md p-3 h-full bg-muted/20">
                    <ul className="list-disc ml-5 space-y-1 text-xs text-muted-foreground">
                      <li>Use variáveis como <code className="bg-gray-100 px-1 rounded">{"{{\u00A0cliente_nome\u00A0}}"}</code> para dados dinâmicos.</li>
                      <li>Formate o documento conforme necessário no Microsoft Word.</li>
                      <li>Cada contrato gerado a partir deste template pode ser personalizado.</li>
                      <li>O template deve conter áreas para assinaturas.</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4">
                <Label htmlFor="is_active" className="cursor-pointer">
                  Template Ativo
                </Label>
                <Switch
                  id="is_active"
                  checked={template.is_active}
                  onCheckedChange={handleSwitchChange}
                />
              </div>
              
              {!isNew && (
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">ID:</span>
                    <span className="font-mono">{template.id}</span>
                  </div>
                  {template.created_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Criado em:</span>
                      <span>{new Date(template.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {template.updated_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Atualizado em:</span>
                      <span>{new Date(template.updated_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2 text-amber-600">
                  <AlertTriangle className="h-4 w-4" />
                  <p className="text-sm font-extrabold">
                    Campos marcados com <span className="text-red-500">*</span> são obrigatórios
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Card for Legal Representatives and Witnesses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Representantes e Testemunhas
              </CardTitle>
              <CardDescription>
                Selecione os usuários autorizados para este modelo
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-extrabold">Representantes Legais</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Usuários que podem assinar como representantes da empresa
                  </p>
                  {isLoadingUsers ? (
                    <div className="flex items-center justify-center h-24">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-900"></div>
                    </div>
                  ) : (
                    <ScrollArea className="h-[150px] border rounded-md p-3">
                      <div className="space-y-2">
                        {users.map((user) => (
                          <div key={user.id} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`legal-${user.id}`} 
                              checked={template.legal_representatives?.includes(user.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  handleLegalRepresentativesChange([
                                    ...(template.legal_representatives || []),
                                    user.id
                                  ]);
                                } else {
                                  handleLegalRepresentativesChange(
                                    (template.legal_representatives || [])
                                      .filter(id => id !== user.id)
                                  );
                                }
                              }}
                            />
                            <Label
                              htmlFor={`legal-${user.id}`}
                              className="cursor-pointer text-sm"
                            >
                              {user.email} {user.role ? `(${user.role})` : ''}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>

                <div>
                  <Label className="text-base font-extrabold">Testemunhas</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Usuários que podem atuar como testemunhas em contratos
                  </p>
                  {isLoadingUsers ? (
                    <div className="flex items-center justify-center h-24">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-900"></div>
                    </div>
                  ) : (
                    <ScrollArea className="h-[150px] border rounded-md p-3">
                      <div className="space-y-2">
                        {users.map((user) => (
                          <div key={user.id} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`witness-${user.id}`} 
                              checked={template.witnesses?.includes(user.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  handleWitnessesChange([
                                    ...(template.witnesses || []),
                                    user.id
                                  ]);
                                } else {
                                  handleWitnessesChange(
                                    (template.witnesses || [])
                                      .filter(id => id !== user.id)
                                  );
                                }
                              }}
                            />
                            <Label
                              htmlFor={`witness-${user.id}`}
                              className="cursor-pointer text-sm"
                            >
                              {user.email} {user.role ? `(${user.role})` : ''}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-[90vw] md:max-w-[80vw] lg:max-w-[800px] w-full h-[80vh] max-h-[800px] flex flex-col">
          <DialogHeader>
            <DialogTitle>Visualizar Template</DialogTitle>
            <DialogDescription>
              Prévia do documento de template
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <iframe
              src={previewUrl}
              style={{ width: '100%', height: '100%', minHeight: '500px', border: 'none' }}
              frameBorder="0"
              allowFullScreen
            ></iframe>
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              onClick={() => window.open(signedDownloadUrl, '_blank')}
              variant="outline"
              className="flex items-center"
            >
              <Download className="mr-2 h-4 w-4" />
              Baixar arquivo original
            </Button>
            <Button
              onClick={() => window.open(previewUrl, '_blank')}
              variant="outline"
              className="flex items-center"
            >
              <Eye className="mr-2 h-4 w-4" />
              Abrir em nova janela
            </Button>
            <Button
              onClick={() => setShowPreviewDialog(false)}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractTemplateForm; 